/*
 * Day One Threads: dependent Style -> Size -> Configuration rules (Phase 2, final).
 * Works on top of Dawn's <variant-selects>/<product-info>; Dawn still fetches the variant,
 * price and URL. This script decides which visible choices count as made by the customer:
 *
 * - A visible Size/Configuration value is never pre-chosen for the customer, unless it is the
 *   only valid value (e.g. "Standard", or Overalls 6/7 which only comes as Shorts).
 * - Options after a missing one stay hidden.
 * - Until every visible option is chosen and Shopify returns a variant: Add to Cart is disabled
 *   with a "Choose a ..." label, Buy It Now is hidden, the cart form's variant id is cleared and
 *   ?variant= is removed from the URL.
 * - Price slot: "Select a style to see pricing" until a style is chosen, then "From $x" for that
 *   style (its smallest size at its lowest-priced option) until the variant is complete.
 * - A polite live region announces the next required choice, or the completed selection.
 *
 * <dot-picker-state> is rendered inside every picker; it runs apply() right after Dawn finishes
 * processing a response (as a microtask, so Dawn's own "Unavailable" text is never painted).
 */
(function () {
  if (customElements.get('dot-picker-state')) return;

  const sections = {};
  const stateFor = (sectionId) =>
    (sections[sectionId] ||= { chosen: {}, initialized: false, autoSelects: 0, fromPrices: {}, lastStatus: '' });

  // Record the customer's choices before Dawn reads the checked inputs.
  document.addEventListener(
    'change',
    (event) => {
      const picker = event.target.closest('variant-selects[data-dependent-options]');
      const group = event.target.closest('[data-option-position]');
      if (!picker || !group) return;

      const state = stateFor(picker.dataset.section);
      const position = Number(group.dataset.optionPosition);
      if (event.isTrusted) state.autoSelects = 0;

      if (position === 1) {
        if (picker.dataset.pickerState === 'empty') state.chosen = {};
      } else {
        state.chosen[position] = true;
      }
    },
    true
  );

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const priceMarkup = (text) =>
    `<div class="price price--large"><div class="price__container"><div class="price__regular"><span class="price-item price-item--regular">${text}</span></div></div></div>`;

  function announce(sectionId, text) {
    const state = stateFor(sectionId);
    if (state.lastStatus === text) return;
    state.lastStatus = text;
    const status = document.getElementById(`dot-picker-status-${sectionId}`);
    if (status) status.textContent = text;
  }

  // Starting price for a style: its smallest size at its lowest-priced option.
  async function loadFromPrice(picker, styleOption) {
    const sectionId = picker.dataset.section;
    const state = stateFor(sectionId);
    const styleId = styleOption.dataset.optionValueId;
    if (state.fromPrices[styleId] !== undefined) return state.fromPrices[styleId];

    const firstSize = picker.querySelector('fieldset[data-option-position="2"] input[type="radio"]');
    if (!firstSize) return null;
    const productInfo = picker.closest('product-info');
    const url = `${productInfo.dataset.url}?section_id=${productInfo.dataset.originalSection || sectionId}&option_values=${styleId},${firstSize.dataset.optionValueId}`;

    try {
      const html = new DOMParser().parseFromString(await (await fetch(url)).text(), 'text/html');
      let cents = null;
      try {
        cents = JSON.parse(html.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null')?.price ?? null;
      } catch (e) {}
      if (cents === null) {
        const prices = Array.from(html.querySelectorAll('fieldset[data-option-position="3"] input[data-variant-price]'))
          .map((input) => Number(input.dataset.variantPrice))
          .filter((n) => !Number.isNaN(n));
        if (prices.length) cents = Math.min(...prices);
      }
      state.fromPrices[styleId] = cents;
      return cents;
    } catch (e) {
      return null;
    }
  }

  function showPrice(productInfo, sectionId, mode, picker) {
    const price = productInfo.querySelector(`#price-${sectionId}`);
    if (!price) return;
    price.classList.remove('hidden');

    if (mode === 'empty') {
      price.innerHTML = priceMarkup('Select a style to see pricing');
      price.style.visibility = '';
    } else if (mode === 'incomplete') {
      // Keep the line's space while the style's starting price loads, so nothing jumps.
      price.style.visibility = 'hidden';
      const styleOption = picker.querySelector('select option[selected][data-option-value-id]');
      if (!styleOption) return;
      loadFromPrice(picker, styleOption).then((cents) => {
        if (!picker.isConnected || picker.style.display === 'none') return;
        if (!productInfo.classList.contains('dot-incomplete')) return;
        price.innerHTML = priceMarkup(cents === null ? 'Select a size to see pricing' : `From ${formatMoney(cents)}`);
        price.style.visibility = '';
      });
    } else {
      price.style.visibility = '';
    }
  }

  function setIncomplete(productInfo, picker, sectionId, prompt, isEmpty = false) {
    const button = productInfo.querySelector(`#ProductSubmitButton-${sectionId}`);
    const buttonText = button?.querySelector('span');
    button?.setAttribute('aria-describedby', `dot-picker-status-${sectionId}`);

    if (prompt) {
      productInfo.classList.add('dot-incomplete');
      if (button) button.setAttribute('disabled', 'disabled');
      if (buttonText) buttonText.textContent = prompt;
      showPrice(productInfo, sectionId, isEmpty ? 'empty' : 'incomplete', picker);
      productInfo
        .querySelectorAll(`#product-form-${sectionId} input[name="id"], #product-form-installment-${sectionId} input[name="id"]`)
        .forEach((input) => (input.value = ''));
      if (productInfo.dataset.updateUrl !== 'false' && productInfo.dataset.url) {
        window.history.replaceState({}, '', productInfo.dataset.url);
      }
      announce(sectionId, `${prompt} to continue.`);
    } else {
      productInfo.classList.remove('dot-incomplete');
      showPrice(productInfo, sectionId, 'complete', picker);
      let variant = null;
      try {
        variant = JSON.parse(picker.querySelector('[data-selected-variant]')?.textContent || 'null');
      } catch (e) {}
      if (variant) {
        const parts = [picker.querySelector('select option[selected]')?.textContent.trim()];
        picker.querySelectorAll('fieldset[data-option-position]').forEach((group) => {
          const checked = group.querySelector('input:checked');
          if (checked && !group.hidden) parts.push(checked.value);
        });
        announce(sectionId, `${parts.filter(Boolean).join(', ')} selected, ${formatMoney(variant.price)}. Ready to add to cart.`);
      }
    }

    document.getElementById(`dot-picker-initial-guard-${sectionId}`)?.remove();
  }

  function apply(picker) {
    if (!picker.isConnected || picker.style.display === 'none') return;
    const productInfo = picker.closest('product-info');
    if (!productInfo) return;

    const sectionId = picker.dataset.section;
    const state = stateFor(sectionId);
    const pickerState = picker.dataset.pickerState;

    if (!state.initialized) {
      state.initialized = true;
      state.chosen = pickerState === 'complete' ? { 2: true, 3: true } : {};
    }

    if (pickerState === 'empty') {
      setIncomplete(productInfo, picker, sectionId, 'Choose a Style', true);
      return;
    }

    let missingGroup = null;
    const groups = picker.querySelectorAll('fieldset[data-option-position]');

    for (const group of groups) {
      const position = Number(group.dataset.optionPosition);
      const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
      group.removeAttribute('aria-describedby');

      if (missingGroup) {
        // Wait until the earlier option is chosen.
        group.hidden = true;
        group.classList.add('hidden');
        if (!state.chosen[position]) radios.forEach((radio) => (radio.checked = false));
        continue;
      }

      if (!radios.length) continue;

      if (radios.length === 1) {
        state.chosen[position] = true;
        if (!radios[0].checked && state.autoSelects < 3) {
          // Only one valid value: choose it and let Dawn load the variant.
          state.autoSelects += 1;
          const button = productInfo.querySelector(`#ProductSubmitButton-${sectionId}`);
          button?.setAttribute('disabled', 'disabled');
          const buttonText = button?.querySelector('span');
          if (buttonText) buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
          radios[0].checked = true;
          radios[0].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        continue;
      }

      if (!state.chosen[position]) radios.forEach((radio) => (radio.checked = false));
      if (!radios.some((radio) => radio.checked)) {
        state.chosen[position] = false;
        missingGroup = group;
      }
    }

    if (missingGroup) {
      missingGroup.setAttribute('aria-describedby', `dot-picker-status-${sectionId}`);
      setIncomplete(productInfo, picker, sectionId, `Choose a ${missingGroup.dataset.optionLabel || 'Option'}`);
    } else if (pickerState === 'incomplete') {
      setIncomplete(productInfo, picker, sectionId, 'Choose an Option');
    } else {
      setIncomplete(productInfo, picker, sectionId, null);
    }
  }

  customElements.define(
    'dot-picker-state',
    class DotPickerState extends HTMLElement {
      connectedCallback() {
        const picker = this.closest('variant-selects[data-dependent-options]');
        if (picker)
          queueMicrotask(() => {
            apply(picker);
            // Phase 3: show or hide the add-on checkboxes for the new selection (assets/dot-addons.js).
            window.DOTAddons?.refresh(picker);
          });
      }
    }
  );
})();
